import { useCallback, useMemo } from "react";
import appLogger from "../../infra/logger/appLogger";
import { appSettings } from "../app/appSettingsManager";
import { useAppSelector } from "../hook";
import useUserInfo from "../user/useUserInfo";
import { areDiscoveryRecommendationFiltersReady, readDiscoveryRecommendationUserId } from "./loadDiscoveryRecommendationRow";
import { PageErrorSource } from "./pageViewModel";
import resolvePageConfiguration from "./resolvePageConfiguration";
import usePageBlockRowsContent from "./usePageBlockRowsContent";
import usePageSettingsStatus from "./usePageSettingsStatus";
const pageControllerLogger = appLogger.withTag("PageController");
/**
 * Page-generic controller behind `useDiscoveryPageController`. Resolves the
 * page configuration from the loaded application settings and drives each
 * supported row, including the recommendation row, through the shared scheduler.
 *
 * Must be rendered inside a Redux provider: the viewer identity that gates the
 * recommendation row is read from the store.
 */
export default function usePageController({ pageKey, isNsfw, loadRecommendations, }) {
    const settingsStatus = usePageSettingsStatus();
    const { isAuthLoaded, isAuthenticated, onlyUserId, guestUserId } = useUserInfo();
    const isUserSettingsLoaded = useAppSelector((state) => state.userSettings.are_user_settings_loaded);
    const settingsFetchStatus = useAppSelector((state) => state.userSettings.are_user_settings_loading);
    const settings = settingsStatus.status === "ready" ? settingsStatus.settings : null;
    const applicationId = settingsStatus.status === "ready" ? settingsStatus.applicationId : null;
    const isSessionAuthenticated = Boolean(isAuthenticated);
    const recommendation = useMemo(() => {
        const viewerId = readDiscoveryRecommendationUserId({
            isAuthLoaded: Boolean(isAuthLoaded),
            isAuthenticated: isSessionAuthenticated,
            onlyUserId,
            guestUserId,
        });
        const isFiltersReady = areDiscoveryRecommendationFiltersReady({
            isAuthenticated: isSessionAuthenticated,
            isUserSettingsLoaded: isUserSettingsLoaded,
            settingsFetchStatus,
        });
        return {
            userId: isFiltersReady ? viewerId : "",
            loadRecommendations,
        };
    }, [isAuthLoaded, isSessionAuthenticated, onlyUserId, guestUserId, isUserSettingsLoaded, settingsFetchStatus, loadRecommendations]);
    const configuration = useMemo(() => (settings === null ? null : resolvePageConfiguration({ settings, pageKey })), [settings, pageKey]);
    const supportedBlocks = configuration?.status === "available" ? configuration.blocks : [];
    // Always called to respect the rules of hooks; idles while the page is not ready.
    const { rows, reportRowEligibility, refreshRows } = usePageBlockRowsContent({
        applicationId,
        pageKey,
        blocks: supportedBlocks,
        isNsfw,
        recommendation,
    });
    const refresh = useCallback(() => {
        refreshRows();
        if (applicationId === null) {
            return Promise.resolve();
        }
        return appSettings.reloadAppSettings({ appId: applicationId }).catch((reloadError) => {
            pageControllerLogger.error("Failed to revalidate application settings", reloadError);
        });
    }, [applicationId, refreshRows]);
    if (settingsStatus.status === "loading") {
        return { status: "loading", reportRowEligibility, refresh };
    }
    if (settingsStatus.status === "error") {
        return { status: "error", source: PageErrorSource.APPLICATION_SETTINGS, reportRowEligibility, refresh };
    }
    if (configuration?.status === "unavailable") {
        return { status: "unavailable", reason: configuration.reason, reportRowEligibility, refresh };
    }
    return {
        status: "ready",
        rows,
        reportRowEligibility,
        refresh,
    };
}
